import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';
import { Send, Eye, Plus, CheckCircle2, Link as LinkIcon, BriefcaseBusiness, FileText, ShieldCheck, Undo2, XCircle, Ban, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { StatTile } from '../components/StatTile';
import { useJobApplications, useJobPostings, useMasterDepartments } from '@/features/recruitment/api';

const AVATAR_COLORS = [
  'bg-[#8B5CF6] text-white',
  'bg-[#10B981] text-white',
  'bg-[#F59E0B] text-white',
  'bg-[#3B82F6] text-white',
  'bg-[#EC4899] text-white',
  'bg-[#6366F1] text-white',
];

function getInitials(name: string) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

interface OfferLetter {
  _id: string;
  candidateId: string;
  /** Set when this offer was auto-created from a Recruitment application
   * reaching the 'hired' stage (see api-recruitment/index.js bridge). Null
   * for offers created standalone from this page. */
  sourceApplicationId?: string | null;
  candidateName: string;
  candidateEmail: string;
  designation: string;
  department: string;
  joiningDate: string;
  salaryStructure: { basic: number; hra: number; da: number; specialAllowance: number; conveyance: number; medicalAllowance: number; grossSalary: number; pf: number; esi: number; professionalTax: number; totalDeductions: number; netPay: number };
  ctcAnnual: number;
  targetRevenue: number;
  incentivePercent: number;
  probationPeriod: string;
  noticePeriod: string;
  workLocation: string;
  reportingTo: string;
  status: string;
  createdAt: string;
  history?: { action: string; at: string; note: string }[];
}

function useOfferLetters() {
  return useQuery({
    queryKey: ['hr', 'offer-letters'],
    queryFn: async () => { const { data } = await api.get('/api/hr-offers'); return data as { data: OfferLetter[] }; },
  });
}

function useCreateOfferLetter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => { const { data } = await api.post('/api/hr-offers', payload); return data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr', 'offer-letters'] }); },
  });
}

interface OfferAccess {
  canRead: boolean;
  canCreate: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  canSend: boolean;
  canRecordResponse: boolean;
  canWithdraw: boolean;
  canManageTemplates: boolean;
}

/** What the signed-in person may do with offers (decided by the server from their HR grants). */
function useOfferAccess() {
  return useQuery({
    queryKey: ['hr', 'offer-access'],
    queryFn: async () => (await api.get<OfferAccess>('/api/hr-offers/access')).data,
    staleTime: 60_000,
  });
}

type OfferAction = 'submit' | 'approve' | 'return' | 'send' | 'accept' | 'reject' | 'withdraw';

/** Offers need a reason when they are sent back, rejected or withdrawn. */
const ACTIONS_NEEDING_REASON: OfferAction[] = ['return', 'reject', 'withdraw'];

function useOfferAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, note }: { id: string; action: OfferAction; note?: string }) => {
      const { data } = await api.post(`/api/hr-offers/${id}/${action}`, note ? { note } : {});
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hr', 'offer-letters'] }); },
  });
}

/** The printable letter needs the login token, so it is fetched and shown from a blob. */
async function previewOfferLetter(id: string) {
  const { data } = await api.get<string>(`/api/hr-offers/${id}/pdf`, { responseType: 'text', transformResponse: (r) => r });
  const url = URL.createObjectURL(new Blob([data], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── HR Document Templates (read-only here; they are edited under Settings > HR > Document Templates) ──

interface HrDocTemplate {
  _id: string;
  id: string;
  department: string;
  docType: string;
  name: string;
  bodyHtml: string;
  updatedAt: string;
}

function useHrTemplates(department?: string, docType?: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['hr', 'templates', department, docType],
    queryFn: async () => {
      const params = new URLSearchParams();
      // The route filters on docType only; department is matched client-side
      // below. Sending it did nothing, which made the filter look broken.
      if (docType) params.set('docType', docType);
      // GET /api/hr/templates answers `{ data: [...] }`, not a bare array
      // (src/api-hr-templates.js). Returning the envelope straight through made
      // `templates` an object, so `templates.find(...)` threw "m.find is not a
      // function" — the ErrorBoundary swallowed it and the modal never appeared.
      // A `= []` default could not save it either: the value was defined, just
      // the wrong shape. Both shapes are accepted so the hook survives the
      // envelope being removed later.
      const { data } = await api.get<{ data?: HrDocTemplate[] } | HrDocTemplate[]>(
        `/api/hr/templates?${params}`,
      );
      if (Array.isArray(data)) return data;
      return Array.isArray(data?.data) ? data.data : [];
    },
  });
}

export function OfferLetterPage() {
  const navigate = useNavigate();
  const { data: access, isLoading: accessLoading } = useOfferAccess();
  const { data, isLoading } = useOfferLetters();
  const createMut = useCreateOfferLetter();
  const actionMut = useOfferAction();
  // An action that needs a reason waits here for the person to write it.
  const [pendingAction, setPendingAction] = useState<{ offer: OfferLetter; action: OfferAction } | null>(null);
  const [reason, setReason] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [onboardingLinkFor, setOnboardingLinkFor] = useState<{ name: string; link: string } | null>(null);
  const [emailDraft, setEmailDraft] = useState<{ to: string; subject: string; html: string } | null>(null);

  // Candidate picker for auto-fill: people in the recruitment pipeline, with their job's title and department.
  const { data: pipeline } = useJobApplications(null);
  const { data: pipelineJobs } = useJobPostings();
  const pipelineCandidates = (pipeline ?? [])
    .filter((a) => a.stage !== 'rejected')
    .map((a) => {
      const job = pipelineJobs?.find((j) => j._id === a.jobId);
      return { id: a._id, name: a.candidateName, email: a.email, designation: job?.title ?? '', department: job?.department ?? '', location: job?.location ?? '' };
    });
  const [selectedCandidate, setSelectedCandidate] = useState('');

  // Form state
  const [form, setForm] = useState({
    candidateName: '', candidateEmail: '', designation: '', department: '',
    joiningDate: '', workLocation: '', reportingTo: '',
    ctcAnnual: '0',
    targetRevenue: '0', incentivePercent: '0',
    probationPeriod: '3 months', noticePeriod: '1 month', additionalTerms: '',
  });
  const setField = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  // Does the chosen department have its own offer letter template? (Only people who manage templates can look.)
  const { data: deptTemplates = [] } = useHrTemplates(form.department || undefined, 'offer_letter', access?.canManageTemplates === true);
  const hasDeptTemplate = !!form.department && deptTemplates.some((t) => t.department.toLowerCase() === form.department.toLowerCase() && (t as { status?: string }).status !== 'draft');
  const { data: departments = [] } = useMasterDepartments();

  const handleCreate = async () => {
    if (!form.candidateName || !form.designation || !form.department || !form.joiningDate) {
      toast.error('Name, designation, department, and joining date are required.');
      return;
    }
    try {
      await createMut.mutateAsync({
        candidateId: selectedCandidate || `manual-${Date.now()}`,
        sourceApplicationId: selectedCandidate || undefined,
        candidateName: form.candidateName,
        candidateEmail: form.candidateEmail,
        designation: form.designation,
        department: form.department,
        joiningDate: form.joiningDate,
        workLocation: form.workLocation,
        reportingTo: form.reportingTo,
        ctcAnnual: Number(form.ctcAnnual) || 0,
        targetRevenue: Number(form.targetRevenue),
        incentivePercent: Number(form.incentivePercent),
        probationPeriod: form.probationPeriod,
        noticePeriod: form.noticePeriod,
        additionalTerms: form.additionalTerms,
      });
      toast.success('Offer letter created as draft.');
      setShowNew(false);
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Failed.'); }
  };

  if (accessLoading || isLoading) return <div className="p-6"><Skeleton className="h-[400px]" /></div>;
  const offers = data?.data || [];

  const statusBadge = (s: string) => {
    const c: Record<string, string> = {
      draft: 'bg-[#F1F3F9] text-[#64748B] border border-[#E2E8F0]',
      pending_approval: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]',
      approved: 'bg-[#EDE9FE] text-[#6825F5] border border-purple-200',
      sent: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE]',
      accepted: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0]',
      rejected: 'bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]',
      withdrawn: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA]',
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${c[s] || 'bg-gray-100'}`}>
        {s.replace(/_/g, ' ')}
      </span>
    );
  };

  const runAction = (o: OfferLetter, action: OfferAction, note?: string) => {
    actionMut.mutate({ id: o._id, action, note }, {
      onSuccess: (result: any) => {
        const done: Record<OfferAction, string> = {
          submit: 'Submitted for approval.',
          approve: 'Offer approved.',
          return: 'Sent back to the drafter.',
          send: 'Offer marked as sent.',
          accept: `${o.candidateName}'s offer marked as accepted.`,
          reject: 'Offer marked as declined.',
          withdraw: 'Offer withdrawn.',
        };
        toast.success(done[action]);
        if (action === 'accept' && result?.onboardingLink) {
          setOnboardingLinkFor({ name: o.candidateName, link: `${window.location.origin}${result.onboardingLink}` });
        }
        if (result?.emailDraft) setEmailDraft(result.emailDraft);
        setPendingAction(null);
        setReason('');
      },
      onError: (err: any) => toast.error(err?.response?.data?.error || 'That did not work.'),
    });
  };
  const startAction = (o: OfferLetter, action: OfferAction) => {
    if (ACTIONS_NEEDING_REASON.includes(action)) { setPendingAction({ offer: o, action }); setReason(''); return; }
    runAction(o, action);
  };

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto pr-1">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <span className="block text-[11px] font-bold tracking-wider text-[#6825F5] uppercase">
              OFFER LETTERS
            </span>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[#11183F]">
              Create, send, and manage offer letters.
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          {access?.canManageTemplates && (
            <Link to="/app/settings/hr/templates">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full border border-[#E8E8F5] bg-white px-4 py-2 text-xs font-semibold text-[#475569] shadow-sm transition-all hover:bg-[#F8F9FE] hover:text-[#11183F] cursor-pointer"
              >
                <FileText className="h-3.5 w-3.5 text-[#6825F5]" />
                <span>Templates</span>
              </button>
            </Link>
          )}
          {access?.canCreate && (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#6825F5] px-5 py-2 text-xs font-semibold text-white shadow-sm shadow-purple-200 transition-all hover:bg-[#581cd6] cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              <span>New Offer Letter</span>
            </button>
          )}
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          icon={FileText}
          label="Total Offers"
          value={offers.length}
          tone="primary"
          index={0}
        />
        <StatTile
          icon={ShieldCheck}
          label="Pending Approval"
          value={offers.filter(o => o.status === 'pending_approval').length}
          tone="warning"
          sublabel="Requires review"
          index={1}
        />
        <StatTile
          icon={Send}
          label="Approved / Sent"
          value={offers.filter(o => ['approved', 'sent'].includes(o.status)).length}
          tone="info"
          sublabel="Awaiting candidate"
          index={2}
        />
        <StatTile
          icon={CheckCircle2}
          label="Accepted"
          value={offers.filter(o => o.status === 'accepted').length}
          tone="success"
          sublabel="Ready for onboarding"
          index={3}
        />
      </div>

      <Card className="rounded-2xl border border-[#E8E8F5] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-[#6825F5]" />
            <h2 className="text-lg font-bold tracking-tight text-[#11183F]">
              All Offer Letters ({offers.length})
            </h2>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden border border-[#F1F3F9]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#F8F9FE] text-[#747C9C] font-bold text-[11px] tracking-wider uppercase border-b border-[#F1F3F9]">
                <tr>
                  <th className="px-5 py-3.5">Candidate</th>
                  <th className="px-4 py-3.5">Designation</th>
                  <th className="px-4 py-3.5">Dept</th>
                  <th className="px-4 py-3.5">Joining</th>
                  <th className="px-4 py-3.5 text-right">CTC (Annual)</th>
                  <th className="px-4 py-3.5">Status</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F3F9]">
                {offers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm font-medium text-[#747C9C]">
                      No offer letters yet. Click "New Offer Letter" to create one.
                    </td>
                  </tr>
                )}
                {offers.map((o) => {
                  const initials = getInitials(o.candidateName);
                  const colorClass = getAvatarColor(o.candidateName || o._id);
                  return (
                    <tr key={o._id} className="hover:bg-[#F8F9FE]/60 transition-colors">
                      <td className="px-5 py-3.5 font-medium">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${colorClass}`}
                          >
                            {initials}
                          </div>
                          <div>
                            <span className="block text-sm font-semibold text-[#11183F] leading-tight">
                              {o.candidateName}
                            </span>
                            {o.sourceApplicationId && (
                              <span className="inline-flex items-center gap-1 mt-0.5 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-[#6825F5]">
                                <BriefcaseBusiness className="h-2.5 w-2.5" /> From Recruitment
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-[#475569]">{o.designation}</td>
                      <td className="px-4 py-3.5 text-sm text-[#475569] capitalize">{o.department}</td>
                      <td className="px-4 py-3.5 text-sm text-[#475569]">
                        {new Date(o.joiningDate).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-4 py-3.5 text-right font-mono font-semibold text-[#11183F]">
                        ₹{(o.ctcAnnual || (o.salaryStructure?.grossSalary || 0) * 12)?.toLocaleString('en-IN') || '0'}
                      </td>
                      <td className="px-4 py-3.5">{statusBadge(o.status)}</td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={async () => {
                              if (o.sourceApplicationId) {
                                try {
                                  const { data } = await api.get<{ candidateId: string }>(`/api/hr-offers/${o._id}/onboarding-candidate`);
                                  if (data?.candidateId) {
                                    navigate(`/app/hr/onboarding/${data.candidateId}/documents/offer_letter`);
                                    return;
                                  }
                                } catch {}
                              }
                              previewOfferLetter(o._id).catch(() => toast.error('Could not open the letter.'));
                            }}
                            className="p-1.5 text-[#747C9C] hover:text-[#6825F5] hover:bg-[#EDE9FE] rounded-lg transition-colors cursor-pointer"
                            title="Preview / Print"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {o.status === 'draft' && access?.canSubmit && (
                            <button
                              type="button"
                              onClick={() => startAction(o, 'submit')}
                              disabled={actionMut.isPending}
                              className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-[#EDE9FE] px-3 py-1 text-xs font-semibold text-[#6825F5] hover:bg-[#DDD6FE] transition-colors disabled:opacity-50 cursor-pointer"
                            >
                              <Send className="h-3 w-3" /> Submit
                            </button>
                          )}
                          {o.status === 'pending_approval' && access?.canApprove && (
                            <>
                              <button
                                type="button"
                                onClick={() => startAction(o, 'approve')}
                                disabled={actionMut.isPending}
                                className="inline-flex items-center gap-1 rounded-full bg-[#10B981] px-3 py-1 text-xs font-semibold text-white hover:bg-[#059669] transition-colors disabled:opacity-50 cursor-pointer"
                              >
                                <ShieldCheck className="h-3 w-3" /> Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => startAction(o, 'return')}
                                disabled={actionMut.isPending}
                                title="Send back to the drafter"
                                className="p-1.5 text-[#747C9C] hover:text-[#DC2626] hover:bg-[#FEE2E2] rounded-lg transition-colors cursor-pointer"
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          {o.status === 'approved' && access?.canSend && (
                            <button
                              type="button"
                              onClick={() => startAction(o, 'send')}
                              disabled={actionMut.isPending}
                              className="inline-flex items-center gap-1 rounded-full bg-[#6825F5] px-3.5 py-1 text-xs font-semibold text-white hover:bg-[#581cd6] transition-colors disabled:opacity-50 cursor-pointer"
                            >
                              <Send className="h-3 w-3" /> Send
                            </button>
                          )}
                          {o.status === 'sent' && access?.canRecordResponse && (
                            <>
                              <button
                                type="button"
                                onClick={() => startAction(o, 'accept')}
                                disabled={actionMut.isPending}
                                title="Record that the candidate accepted this offer"
                                className="inline-flex items-center gap-1 rounded-full bg-[#10B981] px-3 py-1 text-xs font-semibold text-white hover:bg-[#059669] transition-colors disabled:opacity-50 cursor-pointer"
                              >
                                <CheckCircle2 className="h-3 w-3" /> Accept
                              </button>
                              <button
                                type="button"
                                onClick={() => startAction(o, 'reject')}
                                disabled={actionMut.isPending}
                                title="Record that the candidate declined"
                                className="p-1.5 text-[#747C9C] hover:text-[#DC2626] hover:bg-[#FEE2E2] rounded-lg transition-colors cursor-pointer"
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          {['draft', 'pending_approval', 'approved', 'sent'].includes(o.status) && access?.canWithdraw && (
                            <button
                              type="button"
                              onClick={() => startAction(o, 'withdraw')}
                              disabled={actionMut.isPending}
                              title="Withdraw this offer"
                              className="p-1.5 text-[#747C9C] hover:text-[#EA580C] hover:bg-[#FFEDD5] rounded-lg transition-colors cursor-pointer"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {o.status === 'accepted' && (
                            <span className="text-xs text-[#059669] font-semibold inline-flex items-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Accepted
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <Modal
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        title={pendingAction?.action === 'return' ? 'Send back for changes' : pendingAction?.action === 'reject' ? 'Candidate declined' : 'Withdraw offer'}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPendingAction(null)}>Cancel</Button>
            <Button
              onClick={() => pendingAction && runAction(pendingAction.offer, pendingAction.action, reason.trim())}
              disabled={!reason.trim() || actionMut.isPending}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className="space-y-2 py-2">
          <p className="text-sm text-muted-foreground">
            {pendingAction?.offer.candidateName}: please give a reason. It is saved with the offer.
          </p>
          <textarea
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason"
          />
        </div>
      </Modal>

      {emailDraft && (
        <EmailSendModal
          open={!!emailDraft}
          onClose={() => setEmailDraft(null)}
          initialTo={emailDraft.to}
          initialSubject={emailDraft.subject}
          initialHtml={emailDraft.html}
          onSent={() => setEmailDraft(null)}
        />
      )}
    </div>
  );
}
